import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import MSWManagementTertiaryCollectionStore from "./store/MSWManagementTertiaryCollectionStore";
import { MSWManagementTertiaryVehicleInnerExport } from "./MSWManagementVehiclesTertiaryInnerExport";
import isGHMC, { formatDateTimeWithoutSec, formattedDate, formattedDateTypeTwo, getCurrentDateTime } from "../../../../Common/helper/helpar";
import TableSkeleton from "../../../AllotedMaster/TableSkeleton";

export const MSWManagementTertiaryVehiclesOnRoadInnerReport = () => {
  const location = useLocation();
  const isProd = isGHMC();

  const {
    fetchMSWManagementTertiaryCollectionInnerReport,
    loading,
    MSWManagementTertiaryCollectionInnerReportData,
  } = MSWManagementTertiaryCollectionStore();

  /* ================= PAGINATION ================= */
  const [page, setPage] = useState(0);
  const rowsPerPage = 100;


  const storedFilters = localStorage.getItem(
    "mswTertiaryVehiclesFilters"
  );
  const parsedFilters = storedFilters
    ? JSON.parse(storedFilters)
    : null;

  /* ================= DATE HELPERS ================= */
  const formatDateWithStartTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}T00:00`;
  };

  const formatDateWithCurrentTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}T${String(
      now.getHours()
    ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  };

  /* ================= FETCH ================= */
  const selectedDate = location.state?.date || null;

  useEffect(() => {
    if (!selectedDate) return;

    fetchMSWManagementTertiaryCollectionInnerReport({
      FromDate: (selectedDate),
      ToDate: (selectedDate),
    });
    // eslint-disable-next-line
  }, []);

  /* ================= DYNAMIC COLUMNS ================= */
  const FIXED_KEYS = ["ZoneName", "StationName", "Status", "Type", "Sno","Remarks"];

  const dynamicColumns = useMemo(() => {
    if (!MSWManagementTertiaryCollectionInnerReportData?.length) return [];
    return Object.keys(MSWManagementTertiaryCollectionInnerReportData[0]).filter(
      (key) => !FIXED_KEYS.includes(key)
    );
  }, [MSWManagementTertiaryCollectionInnerReportData]);

  /* ================= DYNAMIC TOTALS ================= */
  const dynamicTotals = useMemo(() => {
    if (!MSWManagementTertiaryCollectionInnerReportData?.length) return {};
    return dynamicColumns.reduce((acc, col) => {
      acc[col] = MSWManagementTertiaryCollectionInnerReportData.reduce(
        (sum, row) => sum + Number(row[col] || 0),
        0
      );
      return acc;
    }, {});
  }, [dynamicColumns, MSWManagementTertiaryCollectionInnerReportData]);

  /* ================= STYLES ================= */
  const headerStyles = {
    fontWeight: 600,
    textAlign: "center",
    border: "1px solid #fff",
    color: "#fff",
    backgroundColor: isProd ? "#0d5da5" : "#EC1117",
  };

  const cellStyles = {
    textAlign: "center",
    border: "1px solid #ddd",
    fontSize: "14px",
  };

  const currentDate = getCurrentDateTime();

  /* ================= RENDER ================= */
  return (
    <div className="content">
      <div className="page-content">
        <div className="d-flex justify-content-end mb-2 mt-3">
          <MSWManagementTertiaryVehicleInnerExport
            data={MSWManagementTertiaryCollectionInnerReportData}
            fromDate={formattedDateTypeTwo(selectedDate)}
            toDate = {formattedDateTypeTwo(selectedDate)}
          />
        </div>

        <div className="card">
          <div className="card-body">
            <div className="top-header p-2">
              <span>Detailed Equipment Deployment Data Report ({formattedDateTypeTwo(selectedDate)}) at ({formatDateTimeWithoutSec(currentDate)})</span>
            </div>

            {loading ? (
              <TableSkeleton />
            ) : (
              <Paper sx={{ border: "1px solid #ccc" }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    {/* ================= HEADER ================= */}
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerStyles}>S.No</TableCell>
                        <TableCell sx={headerStyles}>Corporation</TableCell>
                        <TableCell sx={headerStyles}>Zone</TableCell>
                        <TableCell sx={headerStyles}>Station</TableCell>
                        <TableCell sx={headerStyles}>Status</TableCell>
                        <TableCell sx={headerStyles}>Type</TableCell>

                        {dynamicColumns.map((col) => (
                          <TableCell key={col} sx={headerStyles}>
                            {col}
                          </TableCell>
                        ))}
                         <TableCell sx={headerStyles}>Remarks</TableCell>
                      </TableRow>
                    </TableHead>

                    {/* ================= BODY ================= */}
                    <TableBody>
                      {MSWManagementTertiaryCollectionInnerReportData
                        ?.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor: isProd
                                ? index % 2 === 0
                                  ? "#8fc9ff"
                                  : "#aed8ff"
                                : index % 2 === 0
                                ? "#F8CCCD"
                                : "#ffeded",
                            }}
                          >
                            <TableCell sx={cellStyles}>
                              {page * rowsPerPage + index + 1}
                            </TableCell>
                            <TableCell sx={cellStyles}>
                              {row.CorporationName || "N/A"}
                            </TableCell>
                            <TableCell sx={cellStyles}>
                              {row.ZoneName || "N/A"}
                            </TableCell>
                            <TableCell sx={cellStyles}>
                              {row.StationName || "N/A"}
                            </TableCell>
                            <TableCell sx={cellStyles}>
                              {row.Status || "N/A"}
                            </TableCell>
                            <TableCell sx={cellStyles}>
                              {row.Type || "N/A"}
                            </TableCell>

                            {dynamicColumns.map((col) => (
                              <TableCell key={col} sx={cellStyles}>
                                {row[col] ?? 0}
                              </TableCell>
                            ))}
                            <TableCell sx={cellStyles}>
                              {row.Remarks || "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}

                      {/* ================= TOTAL ROW ================= */}
                      {MSWManagementTertiaryCollectionInnerReportData?.length >
                        0 && (
                        <TableRow sx={{ backgroundColor: "#F8CCCD" }}>
                          <TableCell
                            sx={{ ...cellStyles, fontWeight: "bold" }}
                            colSpan={7}
                          >
                            Total
                          </TableCell>

                          {dynamicColumns.map((col) => (
                            <TableCell
                              key={col}
                              sx={{ ...cellStyles, fontWeight: "bold" }}
                            >
                              {dynamicTotals[col]}
                            </TableCell>
                          ))}
                             <TableCell
                            sx={{ ...cellStyles, fontWeight: "bold" }}
                            colSpan={5}
                          >
                            
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* ================= PAGINATION ================= */}
                <TablePagination
                  component="div"
                  count={
                    MSWManagementTertiaryCollectionInnerReportData?.length || 0
                  }
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[100, 500, 1000]}
                />
              </Paper>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
